import {observer} from "mobx-react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {
    Body,
    Container, ContentRow,
    Footer,
    PaddingFilters
} from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/button_filters/styled";
import FilterButton from "@dropDesk/presentation/components/filters/filter_button";
import React, {useEffect, useRef, useState} from "react";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {TicketConfig} from "@dropDesk/domain/entities/ticket/ticket_config";
import InputClientSearch
    from "src/presentation/pages/ticket/ui/search/search_bar/inputs_client_search";
import InputContactSearch from "src/presentation/pages/ticket/ui/search/search_bar/inputs_contact_search";
import InputSectorSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_sector_search";
import InputAttendantSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_attendant_search";
import InputStatusSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_status_search";
import InputPrioritySearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_priority_search";
import InputProblemSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_problem_search";
import CheckBoxOnlySectorListSearch
    from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/checkbox_only_sector_list_search";
import ButtonHideFilters from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/button_remove_filters";

const ButtonFilters = observer(
    ({
         filterStatus,
         filterPriority,
         setFilterStatus,
         setFilterPriority,
         setFilterAttendant,
         loading,
         onSearch,
         filterAttendant,
         setFilterUserClient,
         filterUserClient,
         filterSector,
         setFilterSector,
         filterDescriptionStatic,
         setFilterDescriptionStatic,
         setListOnlySectorEmpty,
         listOnlySectorEmpty,
         filterClient,
         setFilterClient,
         setVisiblePopupButtonFilter,
         visiblePopupButtonFilter,
     }: {
        setFilterStatus: (filter: TicketConfig | null, withList?: boolean) => void,
        setFilterPriority: (filter: TicketConfig | null, withList?: boolean) => void,
        setFilterAttendant: (filter: UserEntity | null, withList?: boolean) => void,
        setFilterUserClient: (filter: UserEntity | null, withList?: boolean) => void,
        setFilterClient: (filter: ClientEntity | null, withList?: boolean) => void,
        setFilterSector: (filter: SectorEntity | null, withList?: boolean) => void,
        setFilterDescriptionStatic: (filter: TicketsDescriptionStaticEntity | null, withList?: boolean) => void,
        filterStatus: TicketConfig | null,
        filterPriority: TicketConfig | null,
        onSearch: (searchParam?: string) => void,
        loading: boolean,
        filterAttendant: UserEntity | null
        filterUserClient: UserEntity | null
        filterSector: SectorEntity | null
        filterClient: ClientEntity | null
        filterDescriptionStatic: TicketsDescriptionStaticEntity | null,
        setListOnlySectorEmpty: (value: boolean, withList?: boolean) => void,
        listOnlySectorEmpty: boolean
        visiblePopupButtonFilter: boolean
        setVisiblePopupButtonFilter: React.Dispatch<React.SetStateAction<boolean>>
    }) => {

        const [zIndex, setZIndex] = useState<number>(0);
        const appController = useInjection(AppController);
        const colors = appController.theme.colorScheme;
        const timeoutRef = useRef<number | undefined>(undefined);

        useEffect(() => {
            handleClickChange(visiblePopupButtonFilter);
        }, [visiblePopupButtonFilter]);

        const handleClickChange = (open: boolean) => {
            if (open) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                setZIndex(1300);
            } else {
                timeoutRef.current = window.setTimeout(() => {
                    setZIndex(0);
                }, 200);
            }
        };

        const filterCount = [
            filterStatus,
            filterPriority,
            filterAttendant,
            filterUserClient,
            filterSector,
            filterDescriptionStatic,
            filterClient,
            listOnlySectorEmpty || null,
        ].filter(Boolean).length;

        return (

            <FilterButton
                hasFilter={filterCount > 0}
                filterCount={filterCount}
                loading={loading}
                open={visiblePopupButtonFilter}
                onOpenChange={(open) => setVisiblePopupButtonFilter(open)}
                zIndex={zIndex}
                content={
                    <Container border={colors.border} width={appController.isUserClient ? 350 : 532}>
                        <Body>

                            <ContentRow className={'input-filter-client-contact'}>
                                <PaddingFilters>
                                    <InputClientSearch
                                        setFilterClient={setFilterClient}
                                        filterClient={filterClient}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                                <PaddingFilters style={{marginLeft: 16}}>
                                    <InputContactSearch
                                        filterClient={filterClient}
                                        filterUserClient={filterUserClient}
                                        setFilterUserClient={setFilterUserClient}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                            </ContentRow>

                            <ContentRow className={'input-filter-attendant-sector'}>
                                <PaddingFilters>
                                    <InputSectorSearch
                                        setFilterSector={setFilterSector}
                                        filterSector={filterSector}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                                <PaddingFilters style={{marginLeft: 16}}>
                                    <InputAttendantSearch
                                        setFilterAttendant={setFilterAttendant}
                                        filterAttendant={filterAttendant}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                            </ContentRow>

                            <ContentRow className={'input-filter-status-priority'}>
                                <PaddingFilters>
                                    <InputStatusSearch
                                        setFilterStatus={setFilterStatus}
                                        filterStatus={filterStatus}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                                <PaddingFilters style={{marginLeft: 16}}>
                                    <InputPrioritySearch
                                        setFilterPriority={setFilterPriority}
                                        filterPriority={filterPriority}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                            </ContentRow>

                            {!appController.isUserClient && !!appController.user?.company.configurations.ticket.useProblemStatic &&
                                <ContentRow className={'input-filter-problem'}>
                                    <PaddingFilters>
                                        <InputProblemSearch
                                            setFilterDescriptionStatic={setFilterDescriptionStatic}
                                            filterDescriptionStatic={filterDescriptionStatic}
                                            loading={loading}
                                        />
                                    </PaddingFilters>
                                </ContentRow>
                            }
                            <div style={{paddingTop: 22}}>
                                <CheckBoxOnlySectorListSearch
                                    setListOnlySectorEmpty={setListOnlySectorEmpty}
                                    listOnlySectorEmpty={listOnlySectorEmpty}
                                    loading={loading}
                                />
                            </div>

                        </Body>
                        <Footer>
                            <ButtonHideFilters
                                onClick={() => {
                                    setFilterStatus(null, false);
                                    setFilterPriority(null, false);
                                    setFilterAttendant(null, false);
                                    setFilterUserClient(null, false);
                                    setFilterSector(null, false);
                                    setFilterDescriptionStatic(null, false);
                                    setFilterClient(null, false);
                                    setListOnlySectorEmpty(false, false);
                                    onSearch();
                                    setVisiblePopupButtonFilter(false);
                                }}
                                loading={loading}
                            />
                        </Footer>
                    </Container>
                }/>

        )
    })
export default ButtonFilters;
